/**
 * 뷰포트 줌 적용 로직(브라우저별 분기) + 조작 핸들러
 *
 * @params none
 * @return label, applyCurrent, increase, decrease, reset (모두 DOM 적용 포함)
 /** @author utis
 * @see    useUiZoomStore, body.style.zoom(Chromium/Safari), html transform(Firefox)
 */
import { computed, onMounted } from 'vue'
import { storeToRefs } from 'pinia'
import { useUiZoomStore } from '~/store/layout/uiZoom'

const MIN = 50
const MAX = 200
const STEP = 10

const isFirefox = () =>
    typeof navigator !== 'undefined' && /firefox/i.test(navigator.userAgent)

/**
 * 실제 DOM에 배율 적용
 *
 * @params pct 적용할 백분율(50~200)
 * @return void
 * @author utis
 * @see    zoom/transform
 */
function applyZoom(pct: number): void {
    if (typeof document === 'undefined') return
    const lv = Math.max(MIN, Math.min(MAX, Math.round(pct)))
    const factor = lv / 100
    const html = document.documentElement
    const body = document.body
    const app  = document.getElementById('__nuxt') || body

    if (!isFirefox()) {
        body.style.zoom = String(factor)
        html.style.transform = ''
        html.style.transformOrigin = ''
        html.style.overflowX = ''
        app.style.width = ''
    } else {
        body.style.zoom = ''
        html.style.transform = `scale(${factor})`
        html.style.transformOrigin = '0 0'
        html.style.overflowX = 'auto'
        app.style.width = `${100 / factor}%`
    }
}

export function useViewportZoom() {
    const store = useUiZoomStore()
    const { zoomLevel } = storeToRefs(store)

    const label = computed(() => `${zoomLevel.value}%`)

    /**
     * 현재 스토어 값 적용(초기/레이아웃 마운트 시)
     *
     * @params none
     * @return void
     * @author utis
     * @see    store.loadFromStorage, applyZoom
     */
    const applyCurrent = (): void => {
        applyZoom(zoomLevel.value)
    }

    /**
     * 확대(+10%)
     *
     * @params none
     * @return void
     * @author utis
     * @see    applyZoom
     */
    const increase = (): void => {
        const next = Math.min(MAX, zoomLevel.value + STEP)
        if (next !== zoomLevel.value) {
            store.setZoomLevel(next)
            applyZoom(next)
        }
    }

    /**
     * 축소(-10%)
     *
     * @params none
     * @return void
     * @author utis
     * @see    applyZoom
     */
    const decrease = (): void => {
        const next = Math.max(MIN, zoomLevel.value - STEP)
        if (next !== zoomLevel.value) {
            store.setZoomLevel(next)
            applyZoom(next)
        }
    }

    /**
     * 초기화(100%)
     *
     * @params none
     * @return void
     * @author utis
     * @see    applyZoom
     */
    const reset = (): void => {
        store.setZoomLevel(100)
        applyZoom(100)
    }

    onMounted(() => {
        const loaded = store.loadFromStorage()
        applyZoom(loaded)
    })

    return { label, applyCurrent, increase, decrease, reset }
}
